import { useState, useRef, useEffect } from 'react'
import { get, post, patch } from '../lib/api'
import NotificationSettings from '../components/NotificationSettings'

// ── Card MUST be outside AccountSettings to prevent cursor-losing bug ──
function Card({ children, title, t }) {
  return (
    <div style={{ background:t.card, border:`1px solid ${t.cardBorder}`, borderRadius:14, padding:'1.5rem', marginBottom:16, boxShadow:t.shadow }}>
      {title && <div style={{ fontSize:15, fontWeight:700, color:t.text, marginBottom:'1.25rem', paddingBottom:'1rem', borderBottom:`1px solid ${t.border}` }}>{title}</div>}
      {children}
    </div>
  )
}

export default function AccountSettings({ profile, t, onRefresh }) {
  const fileRef = useRef()
  const [form, setForm] = useState({
    full_name:    profile?.full_name || '',
    email:        profile?.email     || '',
    org_name:     profile?.organizations?.name || '',
    office_start: '08:00',
    office_end:   '18:00',
    work_days:    ['mon','tue','wed','thu','fri'],
  })
  const [pwd, setPwd]         = useState({ current:'', newPwd:'', confirm:'' })
  const [showPwd, setShowPwd] = useState({ current:false, newPwd:false, confirm:false })
  const [saving, setSaving]       = useState(false)
  const [savingPwd, setSavingPwd] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg]       = useState('')
  const [pwdMsg, setPwdMsg] = useState('')
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '')

  useEffect(() => {
    if (profile?.org_id) {
      get('/org')
        .then(({ org }) => {
          if (org) setForm(f => ({
            ...f,
            org_name:     org.name || f.org_name,
            office_start: org.office_start || '08:00',
            office_end:   org.office_end   || '18:00',
            work_days:    org.work_days    || ['mon','tue','wed','thu','fri'],
          }))
        })
        .catch(() => {})
    }
  }, [])

  function handle(e) {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true); setMsg('')
    try {
      await patch(`/users/${profile.id}`, { full_name: form.full_name })
      await patch('/org', {
        name:         form.org_name,
        office_start: form.office_start,
        office_end:   form.office_end,
        work_days:    form.work_days,
      })
      setMsg('✅ Profile updated successfully!'); if (onRefresh) onRefresh()
    } catch (err) { setMsg(`❌ ${err.message}`) }
    setSaving(false)
  }

  async function handlePassword(e) {
    e.preventDefault()
    setPwdMsg('')
    if (!pwd.current) return setPwdMsg('❌ Please enter your current password')
    if (!pwd.newPwd)  return setPwdMsg('❌ Please enter a new password')
    if (pwd.newPwd.length < 6) return setPwdMsg('❌ Password must be at least 6 characters')
    if (pwd.newPwd !== pwd.confirm) return setPwdMsg('❌ Passwords do not match')
    setSavingPwd(true)
    try {
      await post('/auth/change-password', { currentPassword: pwd.current, newPassword: pwd.newPwd })
      setPwdMsg('✅ Password changed successfully!'); setPwd({ current:'', newPwd:'', confirm:'' })
    } catch (err) {
      setPwdMsg(`❌ ${err.message === 'Current password is incorrect' ? 'Current password is incorrect' : err.message}`)
    }
    setSavingPwd(false)
  }

  async function handleUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 500 * 1024) return setMsg('❌ File must be under 500KB')
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) return setMsg('❌ Only JPG, PNG or WebP')
    setUploading(true); setMsg('')
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      await patch(`/users/${profile.id}`, { avatar_url: dataUrl })
      setAvatarUrl(dataUrl)
      setMsg('✅ Profile photo updated!')
      if (onRefresh) onRefresh()
    } catch (err) { setMsg(`❌ Upload failed: ${err.message}`) }
    setUploading(false)
  }

  const IS = { display:'block', width:'100%', padding:'11px 14px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none' }
  const LS = { display:'block', fontSize:13, fontWeight:600, color:t.text, marginBottom:6 }

  return (
    <div>
      <div style={{ marginBottom:'1.75rem' }}>
        <h1 style={{ fontSize:24, fontWeight:800, color:t.text, margin:'0 0 4px', letterSpacing:'-0.5px' }}>Account Settings</h1>
        <p style={{ fontSize:14, color:t.textMuted, margin:0 }}>Manage your profile, organisation and security settings</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:20, alignItems:'start' }}>

        {/* Left — avatar */}
        <Card t={t}>
          <div style={{ textAlign:'center' }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:'1rem' }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" style={{ width:96, height:96, borderRadius:'50%', objectFit:'cover', border:'3px solid #4F46E5' }} />
              ) : (
                <div style={{ width:96, height:96, borderRadius:'50%', background:'linear-gradient(135deg,#4F46E5,#6366F1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:38, fontWeight:800, color:'#fff' }}>
                  {profile?.full_name?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
            <div style={{ fontSize:16, fontWeight:700, color:t.text, marginBottom:2 }}>{profile?.full_name}</div>
            <div style={{ fontSize:12, color:t.textMuted, marginBottom:4 }}>{profile?.email}</div>
            <div style={{ fontSize:11, fontWeight:600, background:'#4F46E514', color:'#4F46E5', padding:'3px 10px', borderRadius:99, display:'inline-block', marginBottom:'1.25rem' }}>
              Employer · Admin
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} style={{ display:'none' }} />
            <div style={{ background:'#F8F9FA', border:`2px dashed ${t.border}`, borderRadius:10, padding:'1rem', marginBottom:'0.75rem' }}>
              <div style={{ fontSize:24, marginBottom:4 }}>📷</div>
              <p style={{ fontSize:12, color:t.textMuted, margin:'0 0 8px' }}>JPG, PNG, WebP · Max 500KB</p>
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                style={{ padding:'7px 16px', background:'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:7, fontSize:12, fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                {uploading ? 'Uploading…' : '📤 Upload photo'}
              </button>
            </div>
            {msg && (
              <div style={{ fontSize:12, padding:'8px 12px', borderRadius:8, background:msg.startsWith('✅')?'#ECFDF5':'#FEF2F2', color:msg.startsWith('✅')?'#059669':'#EF4444' }}>
                {msg}
              </div>
            )}
          </div>
        </Card>

        {/* Right — forms */}
        <div>
          <form onSubmit={handleSave}>
            <Card t={t} title="👤 Profile Information">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:'1rem' }}>
                <div>
                  <label style={LS}>Full Name</label>
                  <input name="full_name" value={form.full_name} onChange={handle} style={IS} placeholder="Your full name" />
                </div>
                <div>
                  <label style={LS}>Email</label>
                  <input value={form.email} disabled style={{ ...IS, opacity:0.5, cursor:'not-allowed' }} />
                  <div style={{ fontSize:11, color:t.textMuted, marginTop:3 }}>Email cannot be changed here</div>
                </div>
              </div>
            </Card>

            <Card t={t} title="🏢 Organisation">
              <div>
                <label style={LS}>Organisation Name</label>
                <input name="org_name" value={form.org_name} onChange={handle} style={IS} placeholder="Your company name" />
              </div>
            </Card>

            {msg && (
              <div style={{ fontSize:13, padding:'10px 14px', borderRadius:8, marginBottom:12, background:msg.startsWith('✅')?'#ECFDF5':'#FEF2F2', color:msg.startsWith('✅')?'#059669':'#EF4444', border:`1px solid ${msg.startsWith('✅')?'#A7F3D0':'#FECACA'}` }}>
                {msg}
              </div>
            )}

            <button type="submit" disabled={saving}
              style={{ width:'100%', padding:'13px', background:saving?'#E5E7EB':'linear-gradient(135deg,#4F46E5,#6366F1)', border:'none', borderRadius:8, fontSize:14, fontWeight:700, color:saving?'#9CA3AF':'#fff', cursor:saving?'not-allowed':'pointer', fontFamily:'Inter,system-ui', boxShadow:saving?'none':'0 2px 8px rgba(79,70,229,0.3)', marginBottom:16 }}>
              {saving ? 'Saving…' : '✅ Save Changes'}
            </button>
          </form>


          <Card t={t} title="🕐 Office Hours & Working Days">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:'1rem' }}>
              <div>
                <label style={LS}>Office opens (from)</label>
                <input type="time" name="office_start" value={form.office_start}
                  onChange={handle} style={IS} />
              </div>
              <div>
                <label style={LS}>Office closes (to)</label>
                <input type="time" name="office_end" value={form.office_end}
                  onChange={handle} style={IS} />
              </div>
            </div>
            <div>
              <label style={LS}>Working days</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {[['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']].map(([key, label]) => {
                  const active = (form.work_days || []).includes(key)
                  return (
                    <button key={key} type="button"
                      onClick={() => setForm(prev => ({
                        ...prev,
                        work_days: active
                          ? prev.work_days.filter(d => d !== key)
                          : [...(prev.work_days || []), key]
                      }))}
                      style={{ padding:'7px 14px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'Inter,system-ui',
                        border:`2px solid ${active?'#4F46E5':t.border}`,
                        background:active?'#4F46E514':'transparent',
                        color:active?'#4F46E5':t.textSub }}>
                      {label}
                    </button>
                  )
                })}
              </div>
              <div style={{ fontSize:12, color:t.textMuted, marginTop:8 }}>
                These hours control when meetings can be scheduled. Meetings outside these times will be blocked.
              </div>
            </div>
          </Card>

          {/* Password */}
          <form onSubmit={handlePassword}>
            <Card t={t} title="🔒 Change Password">
              {/* Current password */}
              <div style={{ marginBottom:'1rem' }}>
                <label style={LS}>Current Password</label>
                <div style={{ position:'relative' }}>
                  <input
                    type={showPwd.current ? 'text' : 'password'}
                    value={pwd.current}
                    onChange={e => setPwd(p => ({ ...p, current:e.target.value }))}
                    placeholder="●●●●●●●●"
                    style={{ ...IS, paddingRight:44 }}
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, current:!p.current }))}
                    style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:t.textMuted }}>
                    {showPwd.current ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* New password */}
              <div style={{ marginBottom:'1rem' }}>
                <label style={LS}>New Password</label>
                <div style={{ position:'relative' }}>
                  <input
                    type={showPwd.newPwd ? 'text' : 'password'}
                    value={pwd.newPwd}
                    onChange={e => setPwd(p => ({ ...p, newPwd:e.target.value }))}
                    placeholder="●●●●●●●●"
                    style={{ ...IS, paddingRight:44 }}
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, newPwd:!p.newPwd }))}
                    style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:t.textMuted }}>
                    {showPwd.newPwd ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom:'1rem' }}>
                <label style={LS}>Confirm New Password</label>
                <div style={{ position:'relative' }}>
                  <input
                    type={showPwd.confirm ? 'text' : 'password'}
                    value={pwd.confirm}
                    onChange={e => setPwd(p => ({ ...p, confirm:e.target.value }))}
                    placeholder="●●●●●●●●"
                    style={{ ...IS, paddingRight:44, borderColor: pwd.confirm && pwd.confirm !== pwd.newPwd ? '#EF4444' : t.inputBorder }}
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, confirm:!p.confirm }))}
                    style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:t.textMuted }}>
                    {showPwd.confirm ? '🙈' : '👁️'}
                  </button>
                </div>
                {pwd.confirm && pwd.confirm !== pwd.newPwd && (
                  <div style={{ fontSize:12, color:'#EF4444', marginTop:4 }}>Passwords do not match</div>
                )}
              </div>

              {pwdMsg && (
                <div style={{ fontSize:13, padding:'10px 14px', borderRadius:8, marginBottom:12, background:pwdMsg.startsWith('✅')?'#ECFDF5':'#FEF2F2', color:pwdMsg.startsWith('✅')?'#059669':'#EF4444' }}>
                  {pwdMsg}
                </div>
              )}
              <button type="submit" disabled={savingPwd}
                style={{ width:'100%', padding:'13px', background:savingPwd?'#E5E7EB':'linear-gradient(135deg,#EF4444,#DC2626)', border:'none', borderRadius:8, fontSize:14, fontWeight:700, color:savingPwd?'#9CA3AF':'#fff', cursor:savingPwd?'not-allowed':'pointer', fontFamily:'Inter,system-ui' }}>
                {savingPwd ? 'Changing…' : '🔒 Change Password'}
              </button>
            </Card>
          </form>

          <Card t={t} title="🔔 Notifications">
            <NotificationSettings t={t} />
          </Card>
        </div>
      </div>
    </div>
  )
}
